import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, CheckCircle2 } from 'lucide-react';

const clients = {
  retail: [
    { name: 'Nike', type: 'Retail Brand' },
    { name: 'Skechers', type: 'Retail Brand' },
    { name: 'Bagline', type: 'Retail Brand' },
  ],
  builders: [
    { name: 'A. Shridhar Group', type: 'Builder' },
    { name: 'Shivalik Group', type: 'Builder' },
    { name: 'Venus Infra', type: 'Builder' },
    { name: 'Bluewing', type: 'Builder' },
  ],
  government: [
    { name: 'Government Projects', type: 'Completed multiple signage works for government projects across Gujarat' },
  ],
};

const ClientCard = ({ client, index, category }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className="bg-white border border-gray-100 rounded-2xl p-6 text-center group hover:border-sky-200 hover:shadow-elevated transition-all duration-500"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`client-card-${category}-${index}`}
    >
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        <span className="text-2xl font-['Oswald'] font-bold text-sky-500">
          {client.name.charAt(0)}
        </span>
      </div>
      <h4 className="text-lg font-['Oswald'] font-bold text-slate-800 mb-1 group-hover:text-sky-500 transition-colors duration-300">
        {client.name}
      </h4>
      <p className="text-xs text-slate-500 font-['Manrope']">
        {client.type}
      </p>
    </motion.div>
  );
};

const ClientsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="clients"
      className="py-24 md:py-32 bg-slate-50 relative overflow-hidden"
      data-testid="clients-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Our Clients
          </span>
          <h2 className="section-heading mb-6">
            TRUSTED BY <span className="gradient-text">LEADING BRANDS</span>
          </h2>
          <p className="section-subheading mx-auto">
            Trusted by Leading Brands, Builders & Government Projects Across Gujarat
          </p>
        </motion.div>

        {/* Retail Brands */}
        <div className="mb-16">
          <motion.div
            className="flex items-center gap-3 mb-8"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-sky-500" />
            </div>
            <h3 className="text-xl font-['Oswald'] font-bold text-slate-800 uppercase">
              Retail Brands
            </h3>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {clients.retail.map((client, index) => (
              <ClientCard key={index} client={client} index={index} category="retail" />
            ))}
          </div>
        </div>

        {/* Builders */}
        <div className="mb-16">
          <motion.div
            className="flex items-center gap-3 mb-8"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-violet-500" />
            </div>
            <h3 className="text-xl font-['Oswald'] font-bold text-slate-800 uppercase">
              Builders & Real Estate
            </h3>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {clients.builders.map((client, index) => (
              <ClientCard key={index} client={client} index={index} category="builders" />
            ))}
          </div>
        </div>

        {/* Government */}
        <motion.div
          className="bg-white border border-gray-100 rounded-2xl p-8 md:p-12 text-center shadow-soft"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-sky-500" />
            <h3 className="text-xl md:text-2xl font-['Oswald'] font-bold text-slate-800 uppercase">
              Government Contracts
            </h3>
          </div>
          <p className="text-slate-500 font-['Manrope'] max-w-2xl mx-auto">
            Completed multiple signage works for government projects across Gujarat. 
            Our expertise in large-scale projects makes us a trusted partner for public sector initiatives.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientsSection;
